/**
 * Design System Style Guide
 * 
 * @description
 * Comprehensive preview of all design system components and their variants.
 * Use this page to verify design consistency and component behavior.
 * 
 * @module views/design-system/StyleGuide
 */

import { useState } from 'react';
import {
  Button,
  Input,
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
  Modal,
  Table,
} from '@/components/ui';

export function StyleGuide(): React.ReactElement {
  // Modal state for demo
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Sample data for table demo
  const sampleData = [
    { id: '1', name: 'Royal Casino', domain: 'royal.casino', status: 'active', players: 1243 },
    { id: '2', name: 'Lucky Star', domain: 'lucky.star', status: 'active', players: 856 },
    { id: '3', name: 'Golden Palace', domain: 'golden.palace', status: 'inactive', players: 0 },
  ];

  const columns = [
    { key: 'name', header: 'Casino Name', width: '25%' },
    { key: 'domain', header: 'Domain', width: '25%' },
    { 
      key: 'status', 
      header: 'Status', 
      render: (item: typeof sampleData[0]) => (
        <span className={`badge-${item.status === 'active' ? 'success' : 'error'}`}>
          {item.status}
        </span>
      )
    },
    { key: 'players', header: 'Players', render: (item: typeof sampleData[0]) => item.players.toLocaleString() },
  ];

  return (
    <div className="space-y-12 p-8">
      {/**
       * Header
       */}
      <div className="text-center space-y-4">
        <h1 className="text-4xl font-display font-bold text-foreground">
          <span className="text-gradient">iGaming Design System</span>
        </h1>
        <p className="text-xl text-foreground-muted max-w-2xl mx-auto">
          A dark-mode focused design system with neon and gold accents,
          built for premium Casino & Sports Betting platforms.
        </p>
      </div>

      {/**
       * Color Palette Section
       */}
      <section className="space-y-6">
        <h2 className="text-2xl font-display font-semibold text-foreground">Color Palette</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {/* Background Colors */}
          <Card>
            <CardTitle>Background Colors</CardTitle>
            <CardContent className="space-y-2 mt-4">
              <ColorSwatch name="Background" variable="--background" />
              <ColorSwatch name="Secondary" variable="--background-secondary" />
              <ColorSwatch name="Tertiary" variable="--background-tertiary" />
            </CardContent>
          </Card>

          {/* Text Colors */}
          <Card>
            <CardTitle>Text Colors</CardTitle>
            <CardContent className="space-y-2 mt-4">
              <ColorSwatch name="Foreground" variable="--foreground" />
              <ColorSwatch name="Muted" variable="--foreground-muted" />
              <ColorSwatch name="Subtle" variable="--foreground-subtle" />
            </CardContent>
          </Card>

          {/* Neon Accents */}
          <Card variant="neon" interactive>
            <CardTitle>Neon Accents</CardTitle>
            <CardContent className="space-y-2 mt-4">
              <ColorSwatch name="Neon Cyan" variable="--neon-cyan" />
              <ColorSwatch name="Neon Magenta" variable="--neon-magenta" />
              <ColorSwatch name="Neon Purple" variable="--neon-purple" />
              <ColorSwatch name="Neon Green" variable="--neon-green" />
            </CardContent>
          </Card>

          {/* Gold Accent */}
          <Card variant="gold" interactive>
            <CardTitle>Gold Accent</CardTitle>
            <CardContent className="space-y-2 mt-4">
              <ColorSwatch name="Gold" variable="--gold" />
              <ColorSwatch name="Gold Light" variable="--gold-light" />
              <ColorSwatch name="Gold Dark" variable="--gold-dark" />
            </CardContent>
          </Card>

          {/* Semantic Colors */}
          <Card>
            <CardTitle>Semantic Colors</CardTitle>
            <CardContent className="space-y-2 mt-4">
              <ColorSwatch name="Success" variable="--success" />
              <ColorSwatch name="Warning" variable="--warning" />
              <ColorSwatch name="Error" variable="--error" />
              <ColorSwatch name="Info" variable="--info" />
            </CardContent>
          </Card>

          {/* Border Colors */}
          <Card>
            <CardTitle>Border Colors</CardTitle>
            <CardContent className="space-y-2 mt-4">
              <ColorSwatch name="Border" variable="--border" />
              <ColorSwatch name="Border Hover" variable="--border-hover" />
            </CardContent>
          </Card>
        </div>
      </section>

      {/**
       * Typography Section
       */}
      <section className="space-y-6">
        <h2 className="text-2xl font-display font-semibold text-foreground">Typography</h2>
        
        <Card>
          <CardContent className="space-y-6">
            <div className="space-y-2">
              <p className="text-xs text-foreground-muted uppercase tracking-wider">Display Font (Orbitron)</p>
              <p className="text-4xl font-display font-bold text-foreground">
                Casino Platform
              </p>
            </div>
            <div className="space-y-2">
              <p className="text-xs text-foreground-muted uppercase tracking-wider">Sans Font (Inter)</p>
              <p className="text-lg text-foreground">
                The quick brown fox jumps over the lazy dog.
              </p>
            </div>
            <div className="flex flex-wrap gap-4">
              <div className="space-y-1">
                <p className="text-xs text-foreground-subtle">Heading 1</p>
                <p className="text-2xl font-bold font-display text-foreground">Heading 1</p>
              </div>
              <div className="space-y-1">
                <p className="text-xs text-foreground-subtle">Heading 2</p>
                <p className="text-xl font-semibold font-display text-foreground">Heading 2</p>
              </div>
              <div className="space-y-1">
                <p className="text-xs text-foreground-subtle">Heading 3</p>
                <p className="text-lg font-semibold font-display text-foreground">Heading 3</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </section>

      {/**
       * Buttons Section
       */}
      <section className="space-y-6">
        <h2 className="text-2xl font-display font-semibold text-foreground">Buttons</h2>
        
        <Card>
          <CardHeader>
            <CardTitle>Button Variants</CardTitle>
            <CardDescription>
              Multiple visual styles for different use cases and hierarchy levels.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-4">
              <Button variant="primary">Primary</Button>
              <Button variant="secondary">Secondary</Button>
              <Button variant="outline">Outline</Button>
              <Button variant="ghost">Ghost</Button>
              <Button variant="danger">Danger</Button>
              <Button variant="gold">Gold</Button>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Button Sizes</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap items-center gap-4">
              <Button size="sm">Small</Button>
              <Button size="md">Medium</Button>
              <Button size="lg">Large</Button>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Button States</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-4">
              <Button loading>Loading</Button>
              <Button disabled>Disabled</Button>
              <Button fullWidth>Full Width</Button>
            </div>
          </CardContent>
        </Card>
      </section>

      {/**
       * Inputs Section
       */}
      <section className="space-y-6">
        <h2 className="text-2xl font-display font-semibold text-foreground">Input Fields</h2>
        
        <Card>
          <CardHeader>
            <CardTitle>Input Variants</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <Input label="Default Input" placeholder="Enter text..." />
            <Input 
              label="Input with Error" 
              placeholder="Enter text..." 
              error="This field is required" 
              variant="error"
            />
            <Input 
              label="Input with Helper Text" 
              placeholder="Enter text..." 
              helperText="This is some helpful information."
            />
            <Input 
              label="Disabled Input" 
              placeholder="Cannot edit..." 
              disabled 
            />
          </CardContent>
        </Card>
      </section>

      {/**
       * Cards Section
       */}
      <section className="space-y-6">
        <h2 className="text-2xl font-display font-semibold text-foreground">Cards</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardTitle>Default Card</CardTitle>
            <CardDescription>
              Standard card with default styling.
            </CardDescription>
            <CardContent className="mt-4">
              <p className="text-foreground-muted">
                Card content goes here. This is the body of the card.
              </p>
            </CardContent>
            <CardFooter>
              <Button size="sm">Action</Button>
            </CardFooter>
          </Card>

          <Card variant="neon" interactive>
            <CardTitle>Neon Card</CardTitle>
            <CardDescription>
              Card with neon glow effect on hover.
            </CardDescription>
            <CardContent className="mt-4">
              <p className="text-foreground-muted">
                Card content goes here. This is the body of the card.
              </p>
            </CardContent>
            <CardFooter>
              <Button size="sm" variant="primary">Action</Button>
            </CardFooter>
          </Card>

          <Card variant="gold" interactive>
            <CardTitle>Gold Card</CardTitle>
            <CardDescription>
              Card with gold accent for premium elements.
            </CardDescription>
            <CardContent className="mt-4">
              <p className="text-foreground-muted">
                Card content goes here. This is the body of the card.
              </p>
            </CardContent>
            <CardFooter>
              <Button size="sm" variant="gold">Action</Button>
            </CardFooter>
          </Card>
        </div>
      </section>

      {/**
       * Badges Section
       */}
      <section className="space-y-6">
        <h2 className="text-2xl font-display font-semibold text-foreground">Badges</h2>
        
        <Card>
          <CardContent className="flex flex-wrap gap-3">
            <span className="badge-success">Success</span>
            <span className="badge-warning">Warning</span>
            <span className="badge-error">Error</span>
            <span className="badge-info">Info</span>
            <span className="badge-neon">Neon</span>
            <span className="badge-gold">Gold</span>
          </CardContent>
        </Card>
      </section>

      {/**
       * Table Section
       */}
      <section className="space-y-6">
        <h2 className="text-2xl font-display font-semibold text-foreground">Data Table</h2>
        
        <Card padding="none">
          <Table 
            data={sampleData} 
            columns={columns} 
            getKey={(item) => item.id}
            emptyMessage="No tenants found"
          />
        </Card>
      </section>

      {/**
       * Modal Section
       */}
      <section className="space-y-6">
        <h2 className="text-2xl font-display font-semibold text-foreground">Modal</h2>
        
        <Card>
          <CardContent className="flex justify-center">
            <Button onClick={() => setIsModalOpen(true)}>Open Modal</Button>
          </CardContent>
        </Card>
      </section>

      {/**
       * Stat Cards Section
       */}
      <section className="space-y-6">
        <h2 className="text-2xl font-display font-semibold text-foreground">Stat Cards</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="stat-card">
            <span className="stat-card-label">Total Players</span>
            <span className="stat-card-value text-neon-cyan">24,521</span>
          </Card>
          <Card className="stat-card">
            <span className="stat-card-label">Active Tenants</span>
            <span className="stat-card-value text-gold">12</span>
          </Card>
          <Card className="stat-card">
            <span className="stat-card-label">Revenue (24h)</span>
            <span className="stat-card-value">$125,430</span>
          </Card>
          <Card className="stat-card">
            <span className="stat-card-label">Pending Withdrawals</span>
            <span className="stat-card-value text-warning">47</span>
          </Card>
        </div>
      </section>

      {/**
       * Modal Demo
       */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title="Confirm Action"
        description="Please confirm that you want to proceed with this action."
        footer={
          <>
            <Button variant="ghost" onClick={() => setIsModalOpen(false)}>
              Cancel
            </Button>
            <Button variant="primary" onClick={() => setIsModalOpen(false)}>
              Confirm
            </Button>
          </>
        }
      >
        <p className="text-foreground-muted">
          This is a modal dialog. It can contain any content and is useful for
          confirmations, forms, or displaying detailed information.
        </p>
      </Modal>
    </div>
  );
}

/**
 * Color Swatch Component
 */
function ColorSwatch({ name, variable }: { name: string; variable: string }): React.ReactElement {
  return (
    <div className="flex items-center gap-3">
      <div
        className="w-10 h-10 rounded-lg border border-border"
        style={{ backgroundColor: `hsl(var(${variable}))` }}
      />
      <div className="flex flex-col">
        <span className="text-sm text-foreground">{name}</span>
        <span className="text-xs text-foreground-subtle font-mono">{variable}</span>
      </div>
    </div>
  );
}